/**
 * Sistema de Leads Modular - Funções Utilitárias
 * 
 * Funções puras para manipulação de dados de leads
 * Reutilizável em qualquer projeto React
 */

import { Lead, LeadStatus, StatusConfigMap, FilterState } from './types';
import { DEFAULT_STATUS_CONFIG, CSV_HEADERS } from './constants';
import { supabase, isSupabaseConnected } from '../../lib/supabase';

/**
 * Carrega leads do Supabase
 */
export const loadLeads = async (storageKey: string): Promise<Lead[]> => {
  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado, usando localStorage como fallback');
    return loadLeadsFromLocalStorage(storageKey);
  }

  try {
    const { data, error } = await supabase
      .from('ibx_leads')
      .select('*')
      .order('data_hora', { ascending: false });

    if (error) throw error;

    // Mapear dados do Supabase para o formato Lead
    return (data || []).map(row => ({
      id: row.id,
      nome: row.nome,
      telefone: row.telefone || '',
      email: row.email,
      servicoInteresse: row.servico_interesse || '',
      paginaOrigem: row.pagina_origem,
      dataHora: row.data_hora,
      origem: row.origem,
      responsavel: row.responsavel || '',
      observacoes: row.observacoes || '',
      status: row.status as LeadStatus,
      botaoClicado: row.botao_clicado,
      utmSource: row.utm_source,
      utmMedium: row.utm_medium,
      utmCampaign: row.utm_campaign,
    }));
  } catch (error) {
    console.error('Erro ao carregar leads do Supabase:', error);
    return loadLeadsFromLocalStorage(storageKey);
  }
};

/**
 * Salva lead no Supabase (insert)
 */
export const saveLead = async (lead: Omit<Lead, 'id' | 'dataHora'>): Promise<Lead | null> => {
  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado');
    return null;
  }

  try {
    const dbData = {
      nome: lead.nome,
      email: lead.email,
      telefone: lead.telefone || null,
      servico_interesse: lead.servicoInteresse || null,
      pagina_origem: lead.paginaOrigem,
      origem: lead.origem,
      botao_clicado: lead.botaoClicado || null,
      responsavel: lead.responsavel || null,
      status: lead.status,
      observacoes: lead.observacoes || null,
      utm_source: lead.utmSource || null,
      utm_medium: lead.utmMedium || null,
      utm_campaign: lead.utmCampaign || null,
    };

    const { data, error } = await supabase
      .from('ibx_leads')
      .insert(dbData)
      .select()
      .single();

    if (error) throw error;

    // Retornar lead criado no formato da aplicação
    return {
      id: data.id,
      nome: data.nome,
      telefone: data.telefone || '',
      email: data.email,
      servicoInteresse: data.servico_interesse || '',
      paginaOrigem: data.pagina_origem,
      dataHora: data.data_hora,
      origem: data.origem,
      responsavel: data.responsavel || '',
      observacoes: data.observacoes || '',
      status: data.status as LeadStatus,
      botaoClicado: data.botao_clicado,
      utmSource: data.utm_source,
      utmMedium: data.utm_medium,
      utmCampaign: data.utm_campaign,
    };
  } catch (error) {
    console.error('Erro ao salvar lead no Supabase:', error);
    return null;
  }
};

/**
 * Atualiza lead no Supabase
 */
export const updateLead = async (leadId: string, updates: Partial<Lead>): Promise<boolean> => {
  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado');
    return false;
  }

  try {
    const dbUpdates: Record<string, any> = {};
    
    if (updates.nome !== undefined) dbUpdates.nome = updates.nome;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.telefone !== undefined) dbUpdates.telefone = updates.telefone || null;
    if (updates.servicoInteresse !== undefined) dbUpdates.servico_interesse = updates.servicoInteresse || null;
    if (updates.responsavel !== undefined) dbUpdates.responsavel = updates.responsavel || null;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.observacoes !== undefined) dbUpdates.observacoes = updates.observacoes || null;

    const { error } = await supabase
      .from('ibx_leads')
      .update(dbUpdates)
      .eq('id', leadId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao atualizar lead no Supabase:', error);
    return false;
  }
};

/**
 * Deleta lead do Supabase
 */
export const deleteLead = async (leadId: string): Promise<boolean> => {
  if (!isSupabaseConnected()) {
    console.warn('Supabase não conectado');
    return false;
  }

  try {
    const { error } = await supabase
      .from('ibx_leads')
      .delete()
      .eq('id', leadId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao deletar lead do Supabase:', error);
    return false;
  }
};

/**
 * Salva leads no localStorage (DEPRECATED - manter apenas para compatibilidade)
 */
export const saveLeads = (leads: Lead[], storageKey: string): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(leads));
  } catch (error) {
    console.error('Erro ao salvar leads:', error);
    throw error;
  }
};

// ============================================
// FUNÇÕES DE FALLBACK (localStorage)
// ============================================

function loadLeadsFromLocalStorage(storageKey: string): Lead[] {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
    return [];
  } catch (error) {
    console.error('Erro ao carregar leads do localStorage:', error);
    return [];
  }
}

// ============================================
// FUNÇÕES UTILITÁRIAS (mantidas)
// ============================================

/**
 * Filtra e ordena leads baseado nos filtros aplicados
 */
export const filterLeads = (
  leads: Lead[], 
  filters: FilterState
): Lead[] => {
  let result = [...leads];

  // Filtro por status
  if (filters.status !== 'todos') {
    result = result.filter((lead) => lead.status === filters.status);
  }

  // Filtro por busca textual
  if (filters.search.trim()) {
    const query = filters.search.toLowerCase();
    result = result.filter(
      (lead) =>
        lead.nome.toLowerCase().includes(query) ||
        lead.email.toLowerCase().includes(query) ||
        lead.telefone.includes(query)
    );
  }

  return result;
};

/**
 * Ordena leads por data
 */
export const sortLeads = (leads: Lead[], sortOrder: 'asc' | 'desc'): Lead[] => {
  return [...leads].sort((a, b) => {
    const dateA = new Date(a.dataHora).getTime();
    const dateB = new Date(b.dataHora).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });
};

/**
 * Calcula contadores de leads por status
 */
export const calculateStatusCounts = (leads: Lead[]): Record<LeadStatus | 'todos', number> => {
  const counts: Record<LeadStatus | 'todos', number> = {
    todos: leads.length,
    novo: 0,
    contatado: 0,
    qualificado: 0,
    proposta: 0,
    ganho: 0,
    perdido: 0,
  };

  leads.forEach((lead) => {
    counts[lead.status]++;
  });

  return counts;
};

/**
 * Gera ID único para novo lead
 */
export const generateLeadId = (): string => {
  return `lead_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Cria novo lead com dados padrão
 */
export const createNewLead = (formData: Omit<Lead, 'id' | 'dataHora'>): Lead => {
  return {
    ...formData,
    id: generateLeadId(),
    dataHora: new Date().toISOString(),
  };
};

/**
 * Valida dados obrigatórios do lead
 */
export const validateLeadData = (formData: Omit<Lead, 'id' | 'dataHora'>): boolean => {
  return formData.nome.trim() !== '' && formData.email.trim() !== '';
};

/**
 * Atualiza propriedade específica de um lead
 */
export const updateLeadProperty = <K extends keyof Lead>(
  leads: Lead[],
  leadId: string,
  property: K,
  value: Lead[K]
): Lead[] => {
  return leads.map((lead) =>
    lead.id === leadId ? { ...lead, [property]: value } : lead
  );
};

/**
 * Remove lead da lista
 */
export const removeLead = (leads: Lead[], leadId: string): Lead[] => {
  return leads.filter((lead) => lead.id !== leadId);
};

/**
 * Exporta leads para CSV
 */
export const exportToCSV = (
  leads: Lead[], 
  statusConfig: StatusConfigMap = DEFAULT_STATUS_CONFIG,
  filename?: string
): void => {
  if (leads.length === 0) {
    console.warn('Nenhum lead para exportar');
    return;
  }

  const rows = leads.map((lead) => [
    lead.nome,
    lead.telefone,
    lead.email,
    lead.servicoInteresse,
    lead.paginaOrigem,
    new Date(lead.dataHora).toLocaleString('pt-BR'),
    lead.origem,
    lead.botaoClicado || '',
    lead.responsavel,
    statusConfig[lead.status]?.label || lead.status,
    lead.observacoes,
    lead.utmSource || '',
    lead.utmMedium || '',
    lead.utmCampaign || '',
  ]);

  const csvContent = [
    CSV_HEADERS.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  const blob = new Blob(['\uFEFF' + csvContent], {
    type: 'text/csv;charset=utf-8;',
  });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename || `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  
  // Limpar URL do blob
  URL.revokeObjectURL(link.href);
};

/**
 * Mescla configuração de status personalizada com padrão
 */
export const mergeStatusConfig = (
  customConfig?: Partial<StatusConfigMap>
): StatusConfigMap => {
  if (!customConfig) return DEFAULT_STATUS_CONFIG;
  
  return {
    ...DEFAULT_STATUS_CONFIG,
    ...customConfig,
  };
};

/**
 * Formata data para exibição em português
 */
export const formatLeadDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString('pt-BR');
  } catch (error) {
    return 'Data inválida';
  }
};

/**
 * Busca lead por ID
 */
export const findLeadById = (leads: Lead[], leadId: string): Lead | undefined => {
  return leads.find(lead => lead.id === leadId);
};

/**
 * Conta leads por período (últimos N dias)
 */
export const countLeadsByPeriod = (leads: Lead[], days: number): number => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  
  return leads.filter(lead => {
    const leadDate = new Date(lead.dataHora);
    return leadDate >= cutoffDate;
  }).length;
};

/**
 * Obtém leads mais recentes
 */
export const getRecentLeads = (leads: Lead[], limit: number = 5): Lead[] => {
  return sortLeads(leads, 'desc').slice(0, limit);
};